import Vue from 'vue';
import Vuex from 'vuex';
import Swiper from 'swiper';
import { firebase, Ref } from '@/lib/db';

import { ObjToArray } from '@/lib/utils';
import { baseStudentPlan } from '@/lib/baseStudentPlan';
import { Upload } from '@/lib/API';

const randInt = () => Math.floor( Math.random() * 100000 );

Vue.use( Vuex );

export default new Vuex.Store( {
  state : {
    sideMenuSwiper : null,

    user        : false,
    userLoading : true,
    isAdmin     : false,

    // An object whose keys are saved course IDs
    savedCourseKeys : null,

    savedSchool : '',

    // Keeps track of every action to the user's saved/planned courses
    courseActionHistory : [],
    /*
		{
			name: '';
			header: string;
			message: string;
			exec: (...args) => Promise;
			undo: (...args) => Promise;
		}
		*/

    // Refers to an object under studentPlans.userID in firebase
    studentPlan        : null,
    loadingStudentPlan : false,

    // Refers to the selected program of study, a string equalling a CTE pathway ID
    // if the student has chosen a program
    chosenProgram        : null,
    loadingChosenProgram : false,

    gradCredits            : [],
    schools                : [],
    schoolsLoading         : true,
    ctePathways            : [],
    ctePathwaysLoading     : true,
    industrySectors        : [],
    industrySectorsLoading : true,

    courses        : [],
    coursesLoading : true,
    courseState    : 'loading',
    searchResults  : null,

    // setSchool refers to the currently selected school in the Career Technical Education and Program
    // of Study pages' top bar dropdown.
    // Can be either 'All' or the ID of a school
    setSchool : '',

    // filterCategories refers to the primary filter options for `filters`
    // In this case, it refers to the Subject, derived from the courses spreadsheet under "Website Catalog Filter"
    filterCategories : [],
    filters          : {
      primaryFilter : {
        name     : 'Subject',
        value    : 'All',
        priority : 10,
        type     : 'list',
        options  : [],
        filter   : ( course, getters ) => {
          const primaryFilter = getters.filters.primaryFilter.value;
          return primaryFilter === 'All' || course.filterCategory === primaryFilter;
        }
      },
      schools : {
        name     : 'Schools',
        value    : 'All',
        priority : 8,
        type     : 'list',
        options  : [],
        filter   : ( course, getters ) => {
          const schoolsFilter = getters.filters.schools.value;
          return schoolsFilter === 'All' || ( course.schools && course.schools[schoolsFilter] ) || ( course.offCampusSchools && schoolsFilter in course.offCampusSchools );
        }
      },
      ag : {
        name     : 'A-G Designation',
        value    : 'All',
        priority : 6,
        type     : 'list',
        options  : [{ label : 'All', value : 'All' }].concat(
          [
            'A - Social Science',
            'B - English',
            'C - Mathematics',
            'D - Science',
            'E - Foreign Language',
            'F - Art',
            'G - Elective'
          ].map( label => ( { label, value : label[0] } ) )
        ),
        filter : ( course, getters ) => {
          const agFilter = getters.filters.ag.value;
          return agFilter === 'All' || course.ag === agFilter;
        }
      },
      honors : {
        name     : 'Honors',
        value    : false,
        priority : 4,
        type     : 'boolean',
        filter   : ( course, getters ) => {
          const honorsFilter = getters.filters.honors.value;
          return !honorsFilter || ( honorsFilter && course.honors );
        }
      },
      ncaa : {
        name     : 'NCAA',
        value    : false,
        priority : 2,
        type     : 'boolean',
        filter   : ( course, getters ) => {
          const ncaaFilter = getters.filters.ncaa.value;
          return !ncaaFilter || ( ncaaFilter && course.ncaa );
        }
      },
      cte : {
        name     : 'CTE',
        value    : false,
        priority : 5,
        type     : 'boolean',
        filter   : ( course, getters ) => {
          const cteFilter = getters.filters.cte.value;
          return !cteFilter || ( cteFilter && course.ctePathways[0] !== '' );
        }
      },
      offsite : {
        name     : 'Offsite',
        value    : false,
        priority : 0,
        type     : 'boolean',
        filter   : ( course, getters ) => {
          const schoolsFilter = getters.filters.schools.value;
          const offsiteFilter = getters.filters.offsite.value;
          if ( schoolsFilter === 'All' ) return true;
          return !offsiteFilter || (
            !( course.schools && schoolsFilter in course.schools )
						&& course.offCampusSchools && schoolsFilter in course.offCampusSchools
          );
        }
      }
    },

    campusSelectState     : 'closed',
    campusSelectModalOpen : false,

    courseInfoModalOpen : false,
    courseInfoState     : 'closed',
    activeCourse        : {},

    giveFeedbackOpen : false,

    userSwiperState : 'closed',

    // For the csv upload.
    // uploadErrorsState one of the following strings: upload | loading | success | error | fail
    // error is for input error, fail is for unknown application errors.
    uploadOpened      : false,
    uploadErrors      : [],
    uploadErrorsState : 'upload',

    // Used on the CTE Overview page, for the AddSectorModal component
    modalState : 'browse',
    modalStep  : 'open',
    newSector  : {		// IDK why this is named newSector when its only used for editing, should be changed obviously
      title   : null,
      iconUrl : null,
      fileUrl : null,
    },
    isEditing : false,
  },

  getters : {
    courses                : ( state, getters ) => ( getters.coursesLoading ? [] : state.courses ),
    coursesLoading         : state => state.coursesLoading,
    filteredCourses        : ( _, getters ) => getters.appliedFilters( getters.courses ),
    searchResults          : state => state.searchResults,
    getCourseByID          : ( _, getters ) => courseID => getters.courses.find( course => course.id === courseID ),
    getCoursesByGradCredit : ( _, getters ) => gradCreditID => getters.courses.filter( course => course.gradCredits.includes( gradCreditID ) ),
    getCoursesByPathway    : ( _, getters ) => ( ctePathwayID ) => {
      if ( getters.ctePathwaysLoading || getters.coursesLoading ) return [];
      return getters.courses.filter( course => course.ctePathways.find( pid => pid === ctePathwayID ) );
    },

    savedCourseKeys : state => state.savedCourseKeys,
    savedCourses    : ( _, getters ) => getters.courses.filter( course => typeof getters.savedCourseKeys === 'object' && course.id in getters.savedCourseKeys ),
    isCourseSaved   : ( _, getters ) => courseID => courseID in getters.savedCourseKeys,
    plannedCourses  : ( _, getters ) => {
      if ( getters.loadingStudentPlan || getters.coursesLoading ) return [];
      return Object.values( getters.studentPlan )
        .map( section => section.map( ( { courses } ) => courses ) )
        .flat( 2 )
        .filter( c => typeof c === 'string' )
        .map( courseID => getters.getCourseByID( courseID ) )
        .filter( course => !!course );
    },
    isCoursePlanned : ( _, getters ) => courseID => !!getters.plannedCourses.find( course => course.id === courseID ),

    courseActionHistory : state => state.courseActionHistory,

    filters          : state => state.filters,
    filterCategories : state => state.filterCategories,
    appliedFilters   : ( _, getters ) => ( courses ) => {
      const filters = Object.values( getters.filters );
      filters.forEach( ( filter ) => {
        // eslint-disable-next-line
				courses = courses.filter( course => filter.filter( course, getters ) );
      } );
      // eslint-disable-next-line
			// courses = courses.filter( course => course.showInCatalog );
      return courses;
    },

    // Returns filters sorted in decreasing priority (ie, Primary Filter will be first)
    filtersInOrder : ( _, getters ) => (
      Object.entries( getters.filters )
        .map( ( [key, filter] ) => ( { ...filter, key } ) )
        .sort( ( f1, f2 ) => ( f1.priority > f2.priority ? -1 : 1 ) )
    ),

    gradCredits       : state => state.gradCredits,
    getGradCreditByID : ( _, getters ) => id => getters.gradCredits.find( gc => gc.id === id ),

    schools        : state => state.schools,
    schoolsLoading : state => state.schoolsLoading,
    getSchoolByID  : ( _, getters ) => schoolID => getters.schools.find( school => school.id === schoolID ),
    user           : state => state.user,
    userLoading    : state => state.userLoading,
    userID         : ( _, getters ) => ( getters.user ? getters.user.uid : null ),
    isAdmin        : state => state.isAdmin,
    savedSchool    : state => state.savedSchool,

    ctePathways            : state => state.ctePathways,
    ctePathwaysLoading     : state => state.ctePathwaysLoading,
    getCTEPathwayByID      : ( _, getters ) => id => ( getters.ctePathways ? getters.ctePathways.find( pathway => pathway.id === id ) : null ),
    industrySectors        : state => state.industrySectors,
    industrySectorsLoading : state => state.industrySectorsLoading,
    getIndustrySectorByID  : ( _, getters ) => sectorID => getters.industrySectors.find( sector => sector.id === sectorID ),

    /**
		 * Returns CTE Pathways filtered by `setSchool` - ie, only return sectors that are offered by `setSchool`
     * Runs when a user is on Program of Study and selects a CTE sector.
	 */
    getCTEPathwaysBySector : ( _, getters ) => ( sectorID ) => {
      if ( getters.ctePathwaysLoading || getters.schoolsLoading ) return [];

      const pathways = getters.ctePathways.filter( pathway => pathway.industrySector === sectorID );
      if ( getters.setSchool === 'All' ) return pathways;

      const schoolObj = getters.getSchoolByID( getters.setSchool );

      // return only the CTE Pathways that contain the currently selected school.
      return pathways.filter( path => schoolObj && schoolObj.pathways[path.id].provides );
    },

    /**
		 * Returns industry sectors filtered by `setSchool` - ie, only return sectors that are offered by `setSchool`
     * Runs when a user selects a school in either the Program of Study or the Career Technical Education.
		 */
    filteredIndustrySectors : ( _, getters ) => {
      if ( getters.industrySectorsLoading || getters.schoolsLoading ) return [];

      const allSectors = getters.industrySectors;
      if ( getters.setSchool === 'All' ) return allSectors;

      const school = getters.getSchoolByID( getters.setSchool );
      // Return only the sectors that contain the currently selected school
      return allSectors.filter( sector => school && school.industrySectors[sector.id].provides );
    },

    giveFeedbackOpen      : state => state.giveFeedbackOpen,
    campusSelectModalOpen : state => state.campusSelectModalOpen,

    courseInfoModalOpen : state => state.courseInfoModalOpen,
    courseInfoState     : state => state.courseInfoState,
    activeCourse        : state => state.activeCourse,

    sideMenuSwiper     : state => state.sideMenuSwiper,
    loadingSectors     : state => state.loadingSectors,
    setSchool          : state => state.setSchool,
    chosenProgram      : state => state.chosenProgram,
    studentPlan        : state => state.studentPlan,
    loadingStudentPlan : state => state.loadingStudentPlan,

    // Returns the sections (ie, the course cards) in order (freshman, then sophomore, etc)
    // along with the year and index
    studentPlanSections : ( _, getters ) => {
      if ( getters.loadingStudentPlan || !getters.studentPlan ) return [];
      const {
        freshman, sophomore, junior, senior
      } = getters.studentPlan;
      const map = ( arr, year ) => arr.map( ( section, i ) => ( { ...section, year, index : i } ) );
      return [map( freshman, 'freshman' ), map( sophomore, 'sophomore' ), map( junior, 'junior' ), map( senior, 'senior' )].flat();
    }
  },

  mutations : {
    /* eslint-disable no-param-reassign */
    setSideMenuSwiper( state, swiper ) {
      state.sideMenuSwiper = swiper;
    },

    destroySideMenuSwiper( state ) {
      state.sideMenuSwiper.destroy( false, false );
    },

    setCourses( state, courses ) {
      state.courses = courses;
    },

    setCoursesLoading( state, value ) {
      state.coursesLoading = value;
    },

    setSchools( state, schools ) {
      state.schools = schools;
    },
    setSchoolsLoading( state, val ) {
      state.schoolsLoading = val;
    },

    setFilterCategories( state, categories ) {
      state.filterCategories = categories;
    },

    setIndustrySectors( state, sectors ) {
      state.industrySectors = sectors;
    },

    setIndustrySectorsLoading( state, val ) {
      state.industrySectorsLoading = val;
    },

    setCTEPathways( state, val ) {
      state.ctePathways = val;
    },
    setCTEPathwaysLoading( state, val ) {
      state.ctePathwaysLoading = val;
    },

    updateUser( state, payload ) {
      const { user, isAdmin } = payload;
      state.user = user;
      state.isAdmin = isAdmin;
    },
    setUserLoading( state, val ) {
      state.userLoading = val;
    },

    updateStore( state, [prop, value] ) {
      state[prop] = value;
    },

    setChosenProgram( state, program ) {
      state.chosenProgram = program;
    },
    setLoadingChosenProgram( state, val ) {
      state.loadingChosenProgram = val;
    },

    setSavedCourseKeys( state, val ) {
      state.savedCourseKeys = val;
    },

    setStudentPlan( state, plan ) {
      state.studentPlan = plan;
    },
    setLoadingStudentPlan( state, val ) {
      state.loadingStudentPlan = val;
    },

    setFilter( state, payload ) {
      const { key, value } = payload;
      state.filters[key].value = value;
    },

    setFilterOptions( state, payload ) {
      const { key, value } = payload;
      state.filters[key].options = value;
    },

    setGiveFeedbackOpen( state, val ) {
      state.giveFeedbackOpen = val;
    },

    uploadFail( state, errors ) {
      state.uploadErrorsState = 'fail';
      state.uploadErrors      = errors;
    },

    closeUpload( state ) {
      state.uploadOpened      = false;
      state.uploadErrors      = [];
      state.uploadErrorsState = 'upload';
    },

    openSectorUpload( state, sector ) {
      state.modalState = 'add-sector';
      if ( sector ) {
        state.isEditing = true;
        state.newSector = Object.assign( {}, sector );
      }
    },

    openEditPathway( state, pathway ) {
      state.modalState = 'edit-pathway';
      if ( pathway ) {
        state.isEditing = true;
      }
    },

    closeSectorUpload( state ) {
      state.modalState = 'browse';
      state.modalStep  = 'open';
      state.newSector = {
        title   : null,
        iconUrl : null,
        fileUrl : null,
      };
      state.isEditing = false;
    },

    openPathwayUpload( state, sector ) {
      state.modalState = 'add-sector';
      if ( sector ) {
        state.isEditing = true;
        state.newSector = Object.assign( {}, sector );
      }
    },

    closedPathwayUpload( state ) {
      state.modalState = 'browse';
      state.modalStep  = 'open';
      state.newSector = {
        title   : null,
        iconUrl : null,
        fileUrl : null,
      };
      state.isEditing = false;
    },

    search( state, results ) {
      state.searchResults = results;
      state.courseState   = 'loaded';
    },

    setSchool( state, key ) {
      state.setSchool = key;
    },

    setGradCredits( state, gcs ) {
      state.gradCredits = gcs;
    },

    setCourseActionHistory( state, actions ) {
      state.courseActionHistory = [...actions];
    },

    popActionFromHistory( state ) {
      state.courseActionHistory.pop();
    }
  },

  actions : {
    initSideMenuSwiper( { state, commit } ) {
      const { sideMenuSwiper } = state;

      if ( sideMenuSwiper && !sideMenuSwiper.destroyed ) {
        sideMenuSwiper.destroy();
      }

			const swiper = new Swiper( '#side-menu-swiper', { // eslint-disable-line
        // auto initialize this swiper
        init         : true,
        initialSlide : 1,

        // swiper settings
        slidesPerView   : 'auto', // don't force every slide to be 100vw
        speed           : 400, // ms for when you let go of swipe
        spaceBetween    : 0, // no space between
        resistanceRatio : 0, // stops you from swiping past all slides
        direction       : 'horizontal',

        threshold : 50, // min distance for scroll to work
      } );

      commit( 'setSideMenuSwiper', swiper );
    },

    destroySideMenuSwiper( { commit } ) {
      commit( 'destroySideMenuSwiper' );
    },

    /**
		 * Sets firebase listeners and binds to the store for various user-specific state
		 * (e.g., savedCourses, savedSchool, chosenProgram, and studentPlan)
		 *
		 * @param {*} param0
		 * @returns {Promise}
		 */
    initAuthListeners : ( { commit, dispatch } ) => new Promise( ( resolve ) => {
      commit( 'setUserLoading', true );
      firebase.auth().onAuthStateChanged( ( user ) => {
        commit( 'setUserLoading', false );
        if ( !user ) {
          const payload = {
            user,
            isAdmin : false
          };

          commit( 'updateUser', payload );
          dispatch( 'clearAuthState' );
          resolve( user );
          return;
        }

        const { uid } = user;

        Ref
          .child( 'savedCourses' )
          .child( uid )
          .on( 'value', ( snapshot ) => {
            const savedCourseKeys = snapshot.val() || {};
            commit( 'updateStore', ['savedCourseKeys', savedCourseKeys] );
          } );

        Ref
          .child( 'savedSchools' )
          .child( uid )
          .on( 'value', ( snap ) => {
            const savedSchool = snap.val() || '';
            if ( savedSchool === '' ) {
              commit( 'updateStore', ['campusSelectState', 'open'] );
              dispatch( 'openCampusSelect' );
            }
            commit( 'updateStore', ['savedSchool', savedSchool] );
          } );

        commit( 'setLoadingChosenProgram', true );
        Ref
          .child( 'chosenPrograms' )
          .child( uid )
          .on( 'value', async ( snap ) => {
            const chosenProgram = snap.val() || null;
            commit( 'setChosenProgram', chosenProgram );
            commit( 'setLoadingChosenProgram', false );
          } );

        commit( 'setLoadingStudentPlan', true );
        Ref
          .child( 'studentPlans' )
          .child( uid )
          .on( 'value', async ( snap ) => {
            let studentPlan = snap.val();
            // Set a new empty student plan if one does not already exist
            if ( !studentPlan ) {
              studentPlan = baseStudentPlan;
              await Ref.child( 'studentPlans' ).child( uid ).set( studentPlan );
            }
            commit( 'setStudentPlan', studentPlan );
            commit( 'setLoadingStudentPlan', false );
          } );

        commit( 'updateStore', ['userSwiperState', 'closed'] );

        dispatch( 'updateUser', user )
          .then( resolve );
      } );
    } ),

    clearAuthState( { commit } ) {
      commit( 'setStudentPlan', {} );
      commit( 'setSavedCourseKeys', {} );
      commit( 'setChosenProgram', null );
      commit( 'setStudentPlan', {} );
    },


    initCatalogListeners( { commit, dispatch } ) {
      // const resolveAfterListen = ( ref, executor ) => new Promise( async ( resolve, reject ) => {
      // 	ref.on( 'value', async ( snapshot ) => {
      // 		try {
      // 			await executor( snapshot );
      // 			resolve();
      // 		}
      // 		catch ( err ) {
      // 			reject( err );
      // 		}
      // 	} );
      // } );

      // const coursesListener = resolveAfterListen( Ref.child( 'courses' ), ( snapshot ) => {
      // 	const courses = snapshot.val() || {};
      // 	commit( 'setCourses', ObjToArray( courses ) );
      // 	commit( 'setCoursesLoading', false );
      // } );

      // const schoolsListener = resolveAfterListen( Ref.child( 'schools' ), ( snapshot ) => {
      // 	const courses = snapshot.val() || {};
      // 	commit( 'setCourses', ObjToArray( courses ) );
      // 	commit( 'setCoursesLoading', false );
      // } );

      // const filterCategoriesListener = resolveAfterListen( Ref.child( 'filterCategories' ), ( snapshot ) => {
      // 	const courses = snapshot.val() || {};
      // 	commit( 'setCourses', ObjToArray( courses ) );
      // 	commit( 'setCoursesLoading', false );
      // } );
      commit( 'setCoursesLoading', true );
      Ref
        .child( 'courses' )
        .on( 'value', ( snapshot ) => {
          let courses = snapshot.val() || {};
          courses = ObjToArray( courses ).filter( c => c.showInCatalog );
          commit( 'setCourses', courses );
          commit( 'setCoursesLoading', false );
          dispatch( 'initFilters' );
        } );

      commit( 'setSchoolsLoading', true );
      Ref
        .child( 'schools' )
        .on( 'value', ( snapshot ) => {
          const schools = snapshot.val() || {};
          commit( 'setSchools', ObjToArray( schools ) );
          commit( 'setSchoolsLoading', false );
          dispatch( 'initFilters' );
        } );

      Ref
        .child( 'filterCategories' )
        .on( 'value', ( snapshot ) => {
          const fcs = snapshot.val() || {};
          commit( 'setFilterCategories', ObjToArray( fcs ) );
          dispatch( 'initFilters' );
        } );

      commit( 'setIndustrySectorsLoading', true );
      Ref
        .child( 'industrySectors' )
        .on( 'value', ( snapshot ) => {
          const is = snapshot.val() || {};
          commit( 'setIndustrySectorsLoading', false );
          commit( 'setIndustrySectors', ObjToArray( is ) );
        } );

      commit( 'setCTEPathwaysLoading', true );
      Ref
        .child( 'ctePathways' )
        .on( 'value', ( snapshot ) => {
          const p = snapshot.val() || {};
          commit( 'setCTEPathways', ObjToArray( p ) );
          commit( 'setCTEPathwaysLoading', false );
        } );

      Ref
        .child( 'gradCredits' )
        .on( 'value', ( snapshot ) => {
          const gcs = snapshot.val() || {};
          commit( 'setGradCredits', ObjToArray( gcs ) );
        } );
    },

    /**
		 * initFilters is required since some filters (e.g., schools) are not
		 * known until we get the data from firebase.
		 * @param {*} param0
		 */
    initFilters( { commit, getters } ) {
      const { schools, filterCategories } = getters;
      const all = { label : 'All', value : 'All' };

      const schoolOptions = [all].concat(
        schools.map( school => ( {
          label : school.name,
          value : school.id
        } ) )
      );
      const schoolPayload = {
        key   : 'schools',
        value : schoolOptions
      };
      commit( 'setFilterOptions', schoolPayload );

      const primaryFilterOptions = [all].concat(
        filterCategories.map( fc => ( {
          label : fc.name,
          value : fc.id
        } ) )
      );
      const primaryFilterPayload = {
        key   : 'primaryFilter',
        value : primaryFilterOptions
      };
      commit( 'setFilterOptions', primaryFilterPayload );
    },

    updateUser( { commit }, user ) {
      const { uid } = user;

      return Promise.all( [
        Ref
          .child( 'users' )
          .child( uid )
          .once( 'value' )
          .then( snapshot => snapshot.val() ),
        Ref
          .child( 'roles' )
          .child( uid )
          .once( 'value' )
          .then( snapshot => snapshot.val() ),
      ] )
        .then( ( data ) => {
          const userObj      = data[0];
          const isAdmin      = ( data[1] === 'admin' );

          if ( !userObj ) {
            return userObj;
          }

          userObj.uid         = uid;
          userObj.displayName = user.displayName;

          const payload = {
            user : userObj,
            isAdmin,
          };

          commit( 'updateUser', payload );

          return userObj;
        } );
    },

    getCatalog( { commit } ) {
      commit( 'updateStore', ['courseState', 'loading'] );

      Ref
        .child( 'courses' )
        .on( 'value', ( snapshot ) => {
          const courses = snapshot.val() || {};

          commit( 'updateStore', ['courses', ObjToArray( courses )] );
          commit( 'updateStore', ['courseState', 'loaded'] );
        } );

      Ref
        .child( 'schools' )
        .on( 'value', ( snapshot ) => {
          const schools = snapshot.val();

          if ( !schools ) {
            return;
          }

          commit( 'updateStore', ['schools', schools] );
        } );
    },

    updateStore( { commit }, [prop, value] ) {
      commit( 'updateStore', [prop, value] );
    },

    toggleSideMenu( { state } ) {
      const { sideMenuSwiper } = state;
      const activeSlide = sideMenuSwiper.activeIndex;
      if ( activeSlide === 1 ) {
        sideMenuSwiper.slidePrev();

        return;
      }
      sideMenuSwiper.slideNext();
    },


    /**
		 * Sets the filter with the given key to the given value.
		 * @param {*} param0
		 * @param {{ key: string; value: string }} payload
		 */
    selectFilter( { commit }, payload ) {
      commit( 'setFilter', payload );
    },

    resetFilters( { commit, getters } ) {
      const filters = getters.filtersInOrder;
      Object.values( filters ).forEach( ( filter ) => {
        // Either two types: list or boolean
        const payload = {
          key   : filter.key,
          value : filter.type === 'list' ? 'All' : false
        };
        commit( 'setFilter', payload );
      } );
    },

    // updateSectors( { commit }, key ) {
    // 	commit( 'updateStore', ['loadingSectors', true] );

    // 	return new Promise( ( resolve ) => {
    // 		Ref
    // 			.child( 'cte' )
    // 			.child( key )
    // 			.on( 'value', snapshot => FilterNode( snapshot ).then( ( results ) => {
    // 				const sectors = ObjToArray( results );

    // 				commit( 'updateStore', ['sectors', sectors] );
    // 				commit( 'updateStore', ['loadingSectors', false] );
    // 				resolve();

    // 			} ) );
    // 	} );
    // },

    openCourseInfo( { commit }, course ) {
      commit( 'updateStore', ['activeCourse', course] );
      commit( 'updateStore', ['courseInfoState', 'open'] );
      commit( 'updateStore', ['courseInfoModalOpen', true] );
    },

    closeCourseInfo( { commit } ) {
      commit( 'updateStore', ['activeCourse', {}] );
      commit( 'updateStore', ['courseInfoState', 'closed'] );
      commit( 'updateStore', ['courseInfoModalOpen', false] );
    },

    openCampusSelect( { commit } ) {
      commit( 'updateStore', ['campusSelectState', 'open'] );
      commit( 'updateStore', ['campusSelectModalOpen', true] );
    },

    closeCampusSelect( { commit } ) {
      commit( 'updateStore', ['campusSelectState', 'closed'] );
      commit( 'updateStore', ['campusSelectModalOpen', false] );
    },

    openGiveFeedback( { commit } ) {
      commit( 'setGiveFeedbackOpen', true );
    },
    closeGiveFeedback( { commit } ) {
      commit( 'setGiveFeedbackOpen', false );
    },

    openSectorUpload( { commit }, sector ) {
      commit( 'openSectorUpload', sector );
    },

    closeSectorUpload( { commit } ) {
      commit( 'closeSectorUpload' );
    },

    /**
		 * @typedef Action
		 * @type {object}
		 * @property {string} header
		 * @property {string} message
		 * @property {(...any) => Promise} exec
		 * @property {(...any) => Promise} undo
		 *
		 * @param {*} param0
		 * @param {Action} action
		 */
    appendActionToHistory( { commit, getters }, action ) {
      const { courseActionHistory } = getters;
      commit( 'setCourseActionHistory', [...courseActionHistory, action] );
    },

    async undoAction( { commit, getters } ) {
      const len = getters.courseActionHistory.length;
      if ( len === 0 ) return;

      const mostRecentAction = getters.courseActionHistory[len - 1];
      await mostRecentAction.undo();
      const postHistory = getters.courseActionHistory;

      // Max size check: we slice the history if it gets too long
      if ( postHistory.length > 100 ) {
        commit( 'setCourseActionHistory', postHistory.slice( len - 2, len - 1 ) );
      }
      else {
        commit( 'setCourseActionHistory', postHistory.slice( 0, len - 1 ) );
      }
    },

    /**
		 * Sets the schools filter to schoolID and saves the school for the current logged in user.
		 * @param {*} param0
		 * @param {string} schoolID
		 * @returns
		 */
    saveSchool( { getters, dispatch }, schoolID ) {
      const { user, savedSchool } = getters;
      if ( savedSchool === schoolID || !schoolID ) return;
      dispatch( 'selectFilter', { key : 'schools', value : schoolID } );
      dispatch( 'setSchool', schoolID );

      if ( !user ) return;
      Ref
        .child( 'savedSchools' )
        .child( user.uid )
        .set( schoolID );
    },

    setSchool( { commit }, key ) {
      commit( 'setSchool', key );
    },

    openUserSwiper( { commit } ) {
      commit( 'updateStore', ['userSwiperState', 'open'] );
    },

    /**
		 *
		 * @param {*} param0
		 * @param {string} key courseID
		 * @returns
		 */
    async saveCourse( { commit, dispatch, state }, courseID ) {
      const { user } = state;

      if ( !user ) {
        commit( 'updateStore', ['userSwiperState', 'open'] );
        return Promise.resolve();
      }
      const { uid } = user;
      await Ref
        .child( 'savedCourses' )
        .child( uid )
        .child( courseID )
        .set( Date.now() );

      const action = {
        name    : 'saveCourse',
        header  : 'Course Saved',
        message : 'You successfully saved a course',
        exec    : async () => dispatch( 'saveCourse', courseID ),
        undo    : async () => dispatch( 'unsaveCourse', courseID )
      };
      return dispatch( 'appendActionToHistory', action );
    },

    /**
		 *
		 * @param {*} param0
		 * @param {string} courseID
		 */
    async unsaveCourse( { getters, dispatch }, courseID ) {
      const { userID } = getters;

      await Ref
        .child( 'savedCourses' )
        .child( userID )
        .child( courseID )
        .remove();

      const action = {
        id      : randInt(),
        name    : 'unsaveCourse',
        header  : 'Course Unsaved',
        message : 'You successfully unsaved a course',
        exec    : async () => dispatch( 'unsaveCourse', courseID ),
        undo    : async () => dispatch( 'saveCourse', courseID )
      };
      return dispatch( 'appendActionToHistory', action );
    },

    /**
		 * Adds a course to a student's plan at the given year/index.
		 * Also saves the course by default.
		 *
		 * @typedef GradeLevel
		 * @type {'freshman' | 'sophomore' | 'junior' | 'senior'}
		 *
		 * @typedef PlanCoursePayload
		 * @type {object}
		 * @property {GradeLevel} year
		 * @property {number} index
		 * @property {string} courseID
		 *
		 * @param {*} param0
		 * @param {PlanCoursePayload & { saveCourse?: boolean }} payload
		 */
    async planCourse( { getters, dispatch }, payload ) {
      if ( !getters.user ) {
        return dispatch( 'openUserSwiper' );
      }

      const { studentPlan } = getters;
      const {
        year, index, courseID, saveCourse = true
      } = payload;

      if ( typeof courseID !== 'string' ) {
        console.error( `Invalid parameter courseID, expected string, got ${typeof courseID}: ${courseID}` );
        return Promise.resolve();
      }

      // If planned courses does not exist in this section (ie, it's empty since
      // does not store empty arrays!), then initialize it to include only courseID.
      // Otherwise, append it to the current list.
      let plannedCourses = studentPlan[year][index].courses;
      if ( !plannedCourses ) {
        plannedCourses = [courseID];
      }
      else {
        if ( plannedCourses.includes( courseID ) ) return Promise.resolve();
        plannedCourses = plannedCourses.concat( [courseID] );
      }

      // Possible race condition between saving/planning for undo actions? ie, plan course should
      // always be after save course but it might not be
      await Promise.all( [
        saveCourse ? dispatch( 'saveCourse', courseID ) : Promise.resolve(),
        dispatch( 'setPlannedCourses', { year, index, courseIDs : plannedCourses } ),
      ] );

      const action = {
        name    : 'planCourse',
        header  : 'Course Planned',
        message : 'You successfully planned a course',
        exec    : async () => dispatch( 'planCourse', payload ),
        undo    : async () => dispatch( 'unplanCourse', payload )
      };
      return dispatch( 'appendActionToHistory', action );
    },

    /**
		 * Sets the courses for a given year/index in studentPlan. Required to separate planCourse and setPlannedCourses because
		 * firebase doesn't seem to like appending to an array. Thus, to add or remove a planned course, you have to replace the
		 * entire array.
		 *
		 * @typedef PlanCoursesPayload
		 * @type {object}
		 * @property {GradeLevel} year
		 * @property {number} index
		 * @property {Array<string>} courseIDs
		 *
		 * @param {*} param0
		 * @param {PlanCoursesPayload} payload
		 */
    setPlannedCourses( { getters }, payload ) {
      const { year, courseIDs, index } = payload;
      const userID = getters.user.uid;

      return Ref
        .child( 'studentPlans' )
        .child( userID )
        .child( year )
        .child( index )
        .child( 'courses' )
        .set( courseIDs );
    },

    /**
		 *
		 * @param {*} param0
		 * @param {PlanCoursePayload} payload
		 */
    async unplanCourse( { getters, dispatch }, payload ) {
      const { studentPlan } = getters;
      const { year, index, courseID } = payload;

      // Cast in case courses is null for some odd reason
      const plannedCourses = studentPlan[year][index].courses || [];
      const i = plannedCourses.findIndex( id => id === courseID );
      if ( i === -1 ) {
        return null;
      }
      const newPayload = { year, index, courseIDs : plannedCourses.filter( id => id !== courseID ) };
      await dispatch( 'setPlannedCourses', newPayload );
      const action = {
        name    : 'planCourse',
        header  : 'Course Planned',
        message : 'You successfully unplanned a course',
        exec    : async () => dispatch( 'unplanCourse', payload ),
        undo    : async () => dispatch( 'planCourse', payload )
      };
      return dispatch( 'appendActionToHistory', action );
    },

    /**
		 * @param {*} param0
		 * @param {string} programID A CTE Pathway ID
		 */
    chooseProgram( { getters }, programID ) {
      const {
        userID, studentPlan, studentPlanSections, chosenProgram, getCoursesByPathway
      } = getters;
      // console.log( programID );
      const oldProgramCourses = getCoursesByPathway( chosenProgram );
      let newProgramCourses = getCoursesByPathway( programID );
      newProgramCourses = JSON.parse( JSON.stringify( newProgramCourses ) );

      // console.log( newProgramCourses );
      // We will continually build the new student plan object
      const newStudentPlan = JSON.parse( JSON.stringify( studentPlan ) );

      // First, remove all planned courses from the current program
      studentPlanSections.forEach( ( section ) => {
        if ( section.courses ) {
          newStudentPlan[section.year][section.index].courses = section.courses
            .filter( courseID => !oldProgramCourses.find( c => c.id === courseID ) );
        }
      } );

      // // Second, add CTE courses to the plan in the following cteCourseType order:
      // // Introductory Course, Concentration Course, Capstone Course
      // newProgramCourses.sort( ( c1, c2 ) => {
      //   if ( c1.cteCourseType === 'Capstone Course' ) return 1;
      //   if ( c2.cteCourseType === 'Capstone Course' ) return -1;
      //   if ( c1.cteCourseType === 'Concentration Course' ) return 1;
      //   if ( c2.cteCourseType === 'Concentration Course' ) return -1;
      //   return 0;
      // } );
      // Second, add CTE courses to the plan in the following reverse progression order:
      // Introductory Course (1), Concentration Course(2), Capstone Course(3,4)
      newProgramCourses.sort( ( c1, c2 ) => c2.progression - c1.progression );
      // newProgramCourses.reverse(); // Reverse so that arr.pop() returns Intro first, and so on
      let i = 0;
      while ( newProgramCourses.length > 0 && i < studentPlanSections.length ) {
        const years = {
          freshman  : 1,
          sophomore : 2,
          junior    : 3,
          senior    : 4
        };
        // Make sure to use the section referenced by newStudentPlan, not what is currently in use
        const { year, index } = studentPlanSections[i];
        const section = newStudentPlan[year][index];

        // Note that some courses prioritize a grad credit other than CTE
        const gcPriority = newProgramCourses[newProgramCourses.length - 1].gradCreditPriority;

        // The current course will be placed in this section only if:
        // 1. gcPriority is available, this section's gradCredits has gcPriority, and a future CTE section is available if
        // this current course isn't the last in the queue
        // 2. gcPriority isn't available (or condition 1 fails), and this section's gradCredits has CTE
        const useSection = section.gradCredits	&& (
				  ( gcPriority
            && section.gradCredits.includes( gcPriority )
            && ( newProgramCourses.length === 1
              || studentPlanSections.slice( i + 1 ).find( s => s.gradCredits && s.gradCredits.includes( '-CTE' ) )
            )
          )
          || section.gradCredits.includes( '-CTE' )
        );
        if ( useSection ) {
          section.courses = section.courses || [];
          const schoolYear = years[year];
          section.courses.push( ...newProgramCourses.filter( pc => pc.progression == schoolYear ).map( pc => pc.id ) );
          const pushedCoursesLen = newProgramCourses.filter( pc => pc.progression == schoolYear ).map( pc => pc.id ).length;
          for ( let k = 0; k < pushedCoursesLen; k++ ) {
            newProgramCourses.pop();
          }
          newStudentPlan[year][index].courses = section.courses;
        }

        // Since we want to put ALL program courses into the plan, we only increment to the next section if another CTE section exists. Thus, if we run out of CTE
        // sections to put courses into, the rest of the program courses are shoved into the last available section.
        // const sectionIsLastCTE = section.gradCredits.includes( '-CTE' ) && !studentPlanSections.slice( i + 1 ).find( s => s.gradCredits && s.gradCredits.includes( '-CTE' ) );
        // if ( !sectionIsLastCTE ) i++;
        // We don't have to worry about the last CTE section since all courses will get dumped according to their progression
        i++;
      }

      // Be sure to save the new program courses as well
      newProgramCourses = getCoursesByPathway( programID ).reduce( ( acc, course ) => {
        acc[course.id] = Date.now();
        return acc;
      }, {} );
      // TODO: Make this atomic
      return Promise.all( [
        Ref.child( 'savedCourses' ).child( userID ).update( newProgramCourses ),
        Ref.child( 'studentPlans' ).child( userID ).set( newStudentPlan ),
        Ref.child( 'chosenPrograms' ).child( userID ).set( programID )
      ] );
    },

    /**
		 * Changes the grad credits associated with a given class in the student's plan
		 *
		 * @param {*} param0
		 * @param {{ year: string; index: number; gradCredit: object, courses: string[] }} payload
		 */
    changeStudentPlanSubject( { getters }, payload ) {
      const { studentPlan, userID } = getters;
      const {
        year, index, gradCredit, courses
      } = payload;

      let newSection;

      const currentSection = studentPlan[year][index];

      // Ensure that we do not change anything if this class is required, as it should be immutable
      if ( currentSection.gradRequired ) return;

      if ( ['-CTE', '-Electives'].includes( gradCredit.id ) ) {
        newSection = {
          ...currentSection,
          label       : 'Elective / CTE',
          gradCredits : ['-CTE', '-Electives'],
          courses
        };
      }
      else {
        newSection = {
          ...currentSection,
          label       : gradCredit.name,
          gradCredits : [gradCredit.id],
          courses
        };
      }

      Ref
        .child( 'studentPlans' )
        .child( userID )
        .child( year )
        .child( index )
        .set( newSection );
    },

    /**
		 * @param {*} param0
		 * @param {{ currYear: string; currIndex: number; targetYear: string; targetIndex: number }} payload
		 */
    async swapStudentPlanSubjects( { getters }, payload ) {
      const { studentPlan, userID } = getters;
      const {
        currYear, currIndex, targetYear, targetIndex
      } = payload;

      const snapshot = JSON.parse( JSON.stringify( studentPlan ) ); // deep copy
      const currSection = snapshot[currYear][currIndex];
      const targetSection = snapshot[targetYear][targetIndex];
      try {
        return Promise.all( [
          Ref
            .child( 'studentPlans' )
            .child( userID )
            .child( currYear )
            .child( currIndex )
            .set( targetSection ),
          Ref
            .child( 'studentPlans' )
            .child( userID )
            .child( targetYear )
            .child( targetIndex )
            .set( currSection )
        ] );
      }
      catch ( err ) {
        console.error( 'Unable to swap subjects' );
        return Ref
          .child( 'studentPlans' )
          .child( userID )
          .set( snapshot );
      }
    },

    async search( { commit, getters }, query ) {
      if ( !query ) {
        commit( 'search', null );
        return;
      }

      query = query.toLowerCase();
      const courses = getters.courses.slice();

      commit( 'updateStore', ['courseState', 'loading'] );
      commit( 'setCoursesLoading', true );
      // In the future, likely an API call. For now just simply search by checking substring in course name
      const SearchCourses = async () => courses.filter( ( course ) => {
        if ( course.name.toLowerCase().includes( query ) ) {
          return true;
        }
        if ( course.id.toLowerCase().includes( query ) ) {
          return true;
        }
        if ( course.industrySector.toLowerCase().includes( query ) ) {
          return true;
        }
        return false;
      } );
      const results = await SearchCourses();

      commit( 'search', results );
      commit( 'setCoursesLoading', false );
    },

    upload( { commit }, csv ) {
      commit( 'updateStore', ['uploadOpened', true] );
      commit( 'updateStore', ['uploadErrorsState', 'loading'] );

      Upload( csv )
        .then( () => {
          commit( 'updateStore', ['uploadErrorsState', 'success'] );
        } )
        .catch( ( res ) => {
          const { status } = res;
          console.log( 'Unable to upload csv', res );

          if ( res instanceof TypeError ) {
            commit( 'updateStore', ['uploadErrorsState', 'error'] );
            return;
          }

          if ( status === 500 ) {
            commit( 'updateStore', ['uploadErrorsState', 'error'] );
            return;
          }

          if ( !res.json ) {
            commit( 'uploadFail' );
            return;
          }

          res.json()
            .then( ( r ) => {
              // Response is currently a json object containing a message and a context object.
              // In the future, should be an array of errors.
              commit( 'uploadFail', [r] );
            } );
        } );
    },

    closeUpload( { commit } ) {
      commit( 'closeUpload' );
    },

    // resetFilters( { commit, state } ) {
    // 	const { filters } = state;

    // 	const defaultFilters = filters.reduce( ( obj, filter ) => {
    // 		const { key, type } = filter;

    // 		switch ( type ) {

    // 			case 'boolean': {
    // 				obj[key] = false;
    // 				break;
    // 			}

    // 			case 'arbitrary': {
    // 				obj[key] = false;
    // 				break;
    // 			}

    // 			case 'list': {
    // 				const defaultVal = filter.options[0].value;
    // 				obj[key] = defaultVal;
    // 				break;
    // 			}

    // 			case 'multi-list': {
    // 				const defaultVal = filter.options[0].value;
    // 				obj[key] = defaultVal;
    // 				break;
    // 			}

    // 			default: {
    // 				break;
    // 			}
    // 		}

    // 		return obj;

    // 	}, {} );

    // 	commit( 'resetFilters', defaultFilters );

    // }

  }
} );
